// AdminNavbar.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, Settings, User, LogOut } from 'lucide-react';
import axios from 'axios';
import AppContext from '../../context/AppContext';
import '../../assests/css/admin/AdminNavbar.css';

const formatTime = (raw) => {
  if (!raw) return '';
  const date = new Date(raw);
  if (isNaN(date)) return raw;
  const diff = Math.floor((Date.now() - date) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
};

const typeIcon = (type) => {
  const map = {
    order:   '📦',
    warning: '⚠️',
    review:  '⭐',
    payment: '💳',
    system:  '⚙️',
    package: '📦',
    message: '💬',
    bell:    '🔔',
    cart:    '🛒',
    user:    '👤',
  };
  return map[type] || '🔔';
};

const mapNavNotification = (n) => ({
  id:      n.id,
  title:   n.title       || 'Notification',
  message: n.description || '',
  time:    formatTime(n.created_at),
  type:    n.icon        || 'bell',
  unread:  n.status === 'unread',
});

const AdminNavbar = () => {
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);
  const navigate    = useNavigate();

  const [isNotificationOpen,   setIsNotificationOpen]   = useState(false);
  const [isProfileOpen,        setIsProfileOpen]         = useState(false);
  const [isSettingsOpen,       setIsSettingsOpen]        = useState(false);
  const [searchFocused,        setSearchFocused]         = useState(false);
  const [searchValue,          setSearchValue]           = useState('');
  const [notificationsEnabled, setNotificationsEnabled]  = useState(true);
  const [twoFactorAuth,        setTwoFactorAuth]         = useState(false);
  const [notifications,        setNotifications]         = useState([]);
  const [notiLoading,          setNotiLoading]           = useState(false);

 // NEW
  const savedAdmin = JSON.parse(localStorage.getItem('adminUser') || 'null');

  const [adminInfo, setAdminInfo] = useState({
    name:  savedAdmin ? `${savedAdmin.firstname} ${savedAdmin.lastname}`.trim() : 'Admin',
    email: savedAdmin?.email || 'admin@example.com',
    image: null,
  });

  const notificationRef = useRef(null);
  const profileRef      = useRef(null);
  const settingsRef     = useRef(null);

  const unreadCount = notifications.filter(n => n.unread).length;
  const hasUnread   = unreadCount > 0;

  // ── FETCH NOTIFICATIONS ───────────────────────────────────
  const fetchNotifications = () => {
    if (notiLoading) return;
    setNotiLoading(true);
    axios
      .get(`${baseUrl}/admin/notification/getnotifications`)
      .then((res) => {
        const raw = Array.isArray(res.data?.notifications) ? res.data.notifications : [];
        setNotifications(raw.map(mapNavNotification));
      })
      .catch((err) => console.error('Navbar notification fetch error:', err))
      .finally(() => setNotiLoading(false));
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [baseUrl]);

  // ── FETCH ADMIN PROFILE ───────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    axios.get(`${baseUrl}/admin/view-adminprofile`, authHeaders)
      .then((res) => {
        if (res.data.success && res.data.profileData) {
          const p = res.data.profileData;
          const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Admin';
          setAdminInfo({
            name:  fullName,
            email: p.email || 'admin@example.com',
            image: p.profileImage ? `${baseUrlForFiles}/${p.profileImage}` : null,
          });
        }
      })
      .catch((err) => console.error('Navbar profile fetch error:', err));
  }, [baseUrl, baseUrlForFiles]);

  // ── Outside-click close ───────────────────────────────────
  useEffect(() => {
    const clickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target))
        setIsNotificationOpen(false);
      if (profileRef.current && !profileRef.current.contains(event.target))
        setIsProfileOpen(false);
      if (settingsRef.current && !settingsRef.current.contains(event.target))
        setIsSettingsOpen(false);
    };
    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  // ── Mark all read ─────────────────────────────────────────
  const markAllRead = (e) => {
    e.stopPropagation();
    const unreadIds = notifications.filter(n => n.unread).map(n => n.id);

    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));

    Promise.all(
      unreadIds.map(id =>
        axios.post(`${baseUrl}/admin/notification/markasreadnotification`, { id })
      )
    ).catch(err => {
      console.error('Mark-all-read error:', err);
      setNotifications(prev =>
        prev.map(n => unreadIds.includes(n.id) ? { ...n, unread: true } : n)
      );
    });
  };

  // ── Dropdown toggles ──────────────────────────────────────
  const toggleNotification = () => {
    const opening = !isNotificationOpen;
    setIsNotificationOpen(opening);
    setIsProfileOpen(false);
    setIsSettingsOpen(false);
    if (opening) fetchNotifications();
  };

  const toggleSettings = () => {
    setIsSettingsOpen(prev => !prev);
    setIsNotificationOpen(false);
    setIsProfileOpen(false);
  };

  const toggleProfile = () => {
    setIsProfileOpen(prev => !prev);
    setIsNotificationOpen(false);
    setIsSettingsOpen(false);
  };

  // ── Search ────────────────────────────────────────────────
  const onSearchKey = (e) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      navigate(`/admin/search?q=${encodeURIComponent(searchValue)}`);
    }
  };

  const clearSearch       = () => setSearchValue('');
  const updateSearch      = (e) => setSearchValue(e.target.value);
  const focusSearch       = () => setSearchFocused(true);
  const blurSearch        = () => setSearchFocused(false);
  const toggleNotiEnabled = () => setNotificationsEnabled(prev => !prev);
  const toggleTwoFactor   = () => setTwoFactorAuth(prev => !prev);

  // ── Notification item click ───────────────────────────────
  const openNotificationItem = (notification) => {
    if (notification.unread) {
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, unread: false } : n)
      );
      axios
        .post(`${baseUrl}/admin/notification/markasreadnotification`, { id: notification.id })
        .catch(err => {
          console.error('Mark-read error:', err);
          setNotifications(prev =>
            prev.map(n => n.id === notification.id ? { ...n, unread: true } : n)
          );
        });
    }
    const routes = {
      order:   '/admin/orders',
      warning: '/admin/products',
      review:  '/admin/reviews',
      payment: '/admin/payments',
      system:  '/admin/store-setting',
      package: '/admin/products',
      message: '/admin/messages',
    };
    navigate(routes[notification.type] || '/admin/notifications');
    setIsNotificationOpen(false);
  };

  const goToAllNotifications = () => {
    navigate('/admin/notifications');
    setIsNotificationOpen(false);
  };

  // ── Profile nav ───────────────────────────────────────────
  const goToProfile      = () => { navigate('/Admin-profile-setting'); setIsProfileOpen(false); };
  const goToStoreSetting = () => { navigate('/admin/store-setting');   setIsSettingsOpen(false); };

  // ── Logout ────────────────────────────────────────────────

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminemail');
    localStorage.removeItem('adminUser');
    navigate('/admin/login', { replace: true });
  };

  // ── Preview notifications ─────────────────────────────────
  const unreadNotifications  = notifications.filter(n => n.unread).slice(0, 4);
  const previewNotifications = unreadNotifications.length > 0
    ? unreadNotifications
    : notifications.slice(0, 4);

  // ── Fallback avatar ───────────────────────────────────────
  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(adminInfo.name)}&background=8b5cf6&color=fff&bold=true`;
  const avatarSrc = adminInfo.image || fallbackAvatar;

  // ── RENDER ────────────────────────────────────────────────
  return (
    <nav className="Navbar-admin-Navbar">

      {/* Left */}
      <div className="navbar-left">
        <div className="welcome-section">
          <h1 className="welcome-title">Welcome! 👋</h1>
          <p className="welcome-subtitle">Here's what's happening today</p>
        </div>
      </div>

      {/* Center – search */}
      <div className="navbar-center">
        <div className={`search-container ${searchFocused ? 'focused' : ''}`}>
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search products, orders, customers..."
            value={searchValue}
            onChange={updateSearch}
            onFocus={focusSearch}
            onBlur={blurSearch}
            onKeyPress={onSearchKey}
            className="search-input"
          />
          {searchValue && (
            <button className="clear-search" onClick={clearSearch}>×</button>
          )}
        </div>
      </div>

      {/* Right */}
      <div className="navbar-right">

        {/* ── Notifications ── */}
        <div className="navbar-item" ref={notificationRef}>
          <button
            className={`icon-button ${hasUnread ? 'has-notification' : ''}`}
            onClick={toggleNotification}
            title="Notifications"
          >
            <Bell size={20} />
            {hasUnread && (
              <span className="notification-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {isNotificationOpen && (
            <div className="dropdown notification-dropdown">
              <div className="dropdown-header">
                <h3>
                  Notifications
                  {hasUnread && (
                    <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#8b5cf6', fontWeight: 600 }}>
                      {unreadCount} unread
                    </span>
                  )}
                </h3>
                {hasUnread && (
                  <button onClick={markAllRead} className="mark-read-btn">
                    Mark all as read
                  </button>
                )}
              </div>

              <div className="notification-list">
                {notiLoading && previewNotifications.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    Loading...
                  </div>
                ) : previewNotifications.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    🎉 No new notifications
                  </div>
                ) : (
                  previewNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onItemClick={openNotificationItem}
                    />
                  ))
                )}
              </div>

              <div className="dropdown-footer">
                <button className="view-all-btn" onClick={goToAllNotifications}>
                  View All Notifications
                  {notifications.length > 0 && ` (${notifications.length})`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Settings ── */}
        <div className="navbar-item" ref={settingsRef}>
          <button className="icon-button" title="Settings" onClick={toggleSettings}>
            <Settings size={20} />
          </button>

          {isSettingsOpen && (
            <div className="dropdown settings-dropdown">
              <div className="dropdown-header"><h3>Settings</h3></div>
              <div className="settings-list">

                <div className="setting-item">
                  <div className="setting-info">
                    <Bell size={20} />
                    <div>
                      <h4>Notifications</h4>
                      <p>Enable push notifications</p>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={notificationsEnabled} onChange={toggleNotiEnabled} />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item clickable" onClick={goToStoreSetting} style={{ cursor: 'pointer' }}>
                  <div className="setting-info">
                    <Settings size={20} />
                    <div>
                      <h4>All Settings</h4>
                      <p>View all settings</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>

        {/* ── Profile ── */}
        <div className="navbar-item profile-item" ref={profileRef}>
          <button className="profile-button" onClick={toggleProfile}>
            <div className="profile-avatar">
              <img src={avatarSrc} alt="Profile" />
            </div>
            <div className="profile-info">
              <span className="profile-name">{adminInfo.name}</span>
              <span className="profile-role">Administrator</span>
            </div>
          </button>

          {isProfileOpen && (
            <div className="dropdown profile-dropdown">
              <div className="profile-dropdown-header">
                <div className="profile-avatar-large">
                  <img src={avatarSrc} alt="Profile" />
                </div>
                <div className="profile-details">
                  <h3>{adminInfo.name}</h3>
                  <p>{adminInfo.email}</p>
                </div>
              </div>

              <div className="profile-menu">
                <button className="profile-menu-item" onClick={goToProfile}>
                  <User size={18} />
                  <span>My Profile</span>
                </button>

                <div className="profile-menu-divider"></div>

                <button className="profile-menu-item logout" onClick={logout} style={{ color: '#ef4444' }}>
                  <LogOut size={18} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </nav>
  );
};

// ── Per-item component ────────────────────────────────────────
const NotificationItem = ({ notification, onItemClick }) => {
  const click = () => onItemClick(notification);
  return (
    <div
      className={`notification-item ${notification.unread ? 'unread' : ''}`}
      onClick={click}
      style={{ cursor: 'pointer' }}
    >
      <div className={`notification-icon ${notification.type}`}>
        {typeIcon(notification.type)}
      </div>
      <div className="notification-content">
        <h4>{notification.title}</h4>
        <p>{notification.message}</p>
        <span className="notification-time">{notification.time}</span>
      </div>
      {notification.unread && (
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: '#8b5cf6', flexShrink: 0, alignSelf: 'center',
        }} />
      )}
    </div>
  );
};

export default AdminNavbar;